import camelcase from "camelcase-keys";
import React, { useState } from "react";

import { ThreatStatusLabel, Time, Icon } from "../../../../../components";
import { HistoryTableRowProps } from "../../../@types/rldynamicanalysis";
import { convertGoodwareClassification } from "../RlCloudSandboxUtil";
import HistoryMenu from "./HistoryMenu";
import HistoryTableRowItem from "./HistoryTableRowItem";
import RelatedAnalysisTable from "./RelatedAnalysisTable";
import RLCSConfiguration from "./RLCSConfiguration";
import { useGetSingleRlcsReport } from "../hooks/useGetSingleRlcsReport";

const HistoryTableRow: React.FC<HistoryTableRowProps> = ({
  data,
  hash,
  isStoredReport,
  isNetworkReport = false,
  onReportClick
}) => {
  const {
    classification,
    analysisTime,
    analysisDuration,
    configuration,
    analysisId,
    platform,
    geolocation,
    locale,
    riskScore,
    interactive
  } = camelcase(data || {});

  const [classificationLabel, classificationIcon]: string[] =
    convertGoodwareClassification(classification);

  const { data: cloudSandboxData } = useGetSingleRlcsReport(
    hash,
    analysisId,
    isNetworkReport
  );
  const relatedAnalysisData = cloudSandboxData?.related_analysis;
  const [showRelatedAnalysis, setShowRelatedAnalysis] = useState(false);

  const items = [
    {
      name: "expand_button",
      component: relatedAnalysisData && (
        <Icon
          name={showRelatedAnalysis ? "chevron-down" : "chevron-right"}
          className="dropdown-toggle__chevron-icon"
          size={16}
          data-testid={
            showRelatedAnalysis
              ? "rlcs_related_analysis_collapse"
              : "rlcs_related_analysis_expand"
          }
        />
      )
    },
    {
      name: "classification",
      component: (
        <ThreatStatusLabel
          showColor={false}
          showOrb
          threatStatus={classificationIcon}
          labelText={classificationLabel}
          riskScore={riskScore}
        />
      )
    },
    { name: "startTime", component: <Time time={analysisTime} /> },
    {
      name: "interactive",
      component: interactive === "true" ? "Interactive" : "Automated"
    },
    { name: "duration", component: `${analysisDuration} seconds` },
    {
      name: "configuration",
      component: (
        <RLCSConfiguration
          configuration={configuration}
          platform={platform}
          geolocation={geolocation}
          locale={locale}
        />
      )
    },
    {
      name: "actions",
      component: (
        <HistoryMenu
          analysisId={analysisId}
          hash={hash}
          isStoredReport={isStoredReport}
          isNetworkReport={isNetworkReport}
        />
      )
    }
  ];

  const handleClick = (name: string) => {
    if (isStoredReport || name === "actions") return;
    onReportClick(analysisId);
  };

  return (
    <>
      <tr>
        {items.map(({ name, component }) => (
          <HistoryTableRowItem
            key={name}
            onClick={
              name === "expand_button"
                ? () => setShowRelatedAnalysis(!showRelatedAnalysis)
                : () => handleClick(name)
            }
          >
            {component}
          </HistoryTableRowItem>
        ))}
      </tr>
      {!!relatedAnalysisData && showRelatedAnalysis && (
        <tr>
          <td
            colSpan={9}
            className="rl-cloud-sandbox__history-table-related-analysis-row"
          >
            <RelatedAnalysisTable data={relatedAnalysisData} />
          </td>
        </tr>
      )}
    </>
  );
};

export default HistoryTableRow;
